import { BayLevelEnum, } from "open-vessel-definition";
import { CONTAINER_HEIGHT_IN_MMM, TCG_IN_MMM } from "../consts";
import { calculateAftBayLayout, } from "../aftView/calculateAftBayLayout";
import { getRowsAndTiersFromSlotKeys } from "../getRowsAndTiersFromSlotKeys";
import { pad2, roundDec, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";
import { bayLevelHasCenterLineRow } from "../bayLevelHasCenterLineRow";
import { calculateTeusFromSlots } from "../calculateTeusFromSlots";
import { getAllThePairedBays } from "../getAllThePairedBays";
import { getIsoBaysUnderLid } from "../getIsoBaysUnderLid";
import { sortRowsArray } from "../sortRowsArray";
/**
 * Derives everything a vessel-particulars sheet needs from an OVD
 * file: one cross-section per group of geometrically identical bays (real TCGs and
 * bottom bases via `calculateAftBayLayout`, synthesized and flagged when the file
 * has none), 20'/40' slot counts and LCGs per bay, and the ship particulars.
 * Pure data — see tedivo-bay-grid-core for the drawing.
 */
export function buildVesselParticularsData({ shipData, sizeSummary, baysData, lidData, }) {
    const sections = buildSections(shipData, sizeSummary, baysData, lidData);
    const bayCounts = buildBayCounts(baysData);
    const particulars = buildParticulars(shipData, baysData);
    return { sections, bayCounts, particulars };
}
/** Synthetic bottom base of the vessel-wide lowest hold tier when the file has no VCGs (only for drawing) */
const SYNTHETIC_BELOW_BASE_MM = 2000;
/** Synthetic bottom base of the vessel-wide lowest deck tier when the file has no VCGs and no hold at all */
const SYNTHETIC_ABOVE_BASE_MM = 16000;
/** Vertical allowance between the top of the hold stacks and the deck bottom base (hatch covers) */
const SYNTHETIC_HATCH_CLEARANCE_MM = 1000;
/** The usable slot keys of a bay-level: not restricted, and no leftover 00 slots on a level without the centerline row (same as the Bay views, see bayLevelHasCenterLineRow) */
function usableSlotKeys(bl, sizeSummary) {
    var _a;
    const hasCenterLineRow = bayLevelHasCenterLineRow(sizeSummary, bl);
    return Object.keys((_a = bl.perSlotInfo) !== null && _a !== void 0 ? _a : {}).filter((key) => { var _a, _b; return !((_b = (_a = bl.perSlotInfo) === null || _a === void 0 ? void 0 : _a[key]) === null || _b === void 0 ? void 0 : _b.restricted) && (hasCenterLineRow || !key.startsWith("00")); });
}
/** The real bottom base of a row, resolved like the Bay views: per row, then common, then the master base of the row's lowest tier */
function resolveRowBottomBase(bl, row, tiersByRow, masterCGs) {
    var _a, _b, _c, _d, _e, _f, _g;
    const bottomTier = tiersByRow[row] ? pad2(tiersByRow[row].minTier) : undefined;
    return ((_g = (_d = (_c = (_b = (_a = bl.perRowInfo) === null || _a === void 0 ? void 0 : _a.each) === null || _b === void 0 ? void 0 : _b[row]) === null || _c === void 0 ? void 0 : _c.bottomBase) !== null && _d !== void 0 ? _d : (_f = (_e = bl.perRowInfo) === null || _e === void 0 ? void 0 : _e.common) === null || _f === void 0 ? void 0 : _f.bottomBase) !== null && _g !== void 0 ? _g : (bottomTier !== undefined ? masterCGs.bottomBases[bottomTier] : undefined));
}
/**
 * Builds the per-level layout data of one bay-level, or undefined when it has no
 * usable slots. Missing CGs never drop the bay: row positions and stack bases are
 * synthesized (uniform 2520 mm pitch, stacked container heights) and flagged as
 * not real, so the drawing shows the geometry but omits the CG numbers. The
 * synthesized bases are anchored on the vessel-wide lowest tier of the level
 * kind, so the same tier lands at the same height in every bay and the sections
 * of a CG-less vessel align perfectly on the sheet.
 */
function buildSectionLevel(bl, masterCGs, sizeSummary, synthAnchor) {
    var _a, _b;
    const slotKeys = usableSlotKeys(bl, sizeSummary);
    if (slotKeys.length === 0)
        return undefined;
    const { rows, tiersByRow } = getRowsAndTiersFromSlotKeys(slotKeys);
    const masterTcgs = bl.level === BayLevelEnum.ABOVE ? masterCGs.aboveTcgs : masterCGs.belowTcgs;
    const each = (_a = bl.perRowInfo) === null || _a === void 0 ? void 0 : _a.each;
    const common = (_b = bl.perRowInfo) === null || _b === void 0 ? void 0 : _b.common;
    const perRow = {};
    rows.forEach((row) => {
        var _a, _b;
        const rowInfo = each === null || each === void 0 ? void 0 : each[row];
        perRow[row] = {
            tcg: (_a = rowInfo === null || rowInfo === void 0 ? void 0 : rowInfo.tcg) !== null && _a !== void 0 ? _a : masterTcgs[row],
            bottomBase: resolveRowBottomBase(bl, row, tiersByRow, masterCGs),
            maxHeight: (_b = rowInfo === null || rowInfo === void 0 ? void 0 : rowInfo.maxHeight) !== null && _b !== void 0 ? _b : common === null || common === void 0 ? void 0 : common.maxHeight,
        };
    });
    // A single missing value makes the whole level synthetic: mixing real and
    // synthesized CGs would distort the drawn geometry.
    const tcgsAreReal = rows.every((row) => { var _a; return ((_a = perRow[row]) === null || _a === void 0 ? void 0 : _a.tcg) !== undefined; });
    if (!tcgsAreReal) {
        rows.forEach((row, idx) => {
            perRow[row].tcg = (idx - (rows.length - 1) / 2) * TCG_IN_MMM;
        });
    }
    const bottomBasesAreReal = rows.every((row) => { var _a; return ((_a = perRow[row]) === null || _a === void 0 ? void 0 : _a.bottomBase) !== undefined; });
    if (!bottomBasesAreReal) {
        // Anchored on the VESSEL-wide lowest tier (not this bay's), so tier N
        // synthesizes to the same height in every bay
        const localMinTier = Math.min(...rows.map((row) => { var _a, _b; return (_b = (_a = tiersByRow[row]) === null || _a === void 0 ? void 0 : _a.minTier) !== null && _b !== void 0 ? _b : Infinity; }));
        const refTier = Number.isFinite(synthAnchor.refTier) ? synthAnchor.refTier : localMinTier;
        rows.forEach((row) => {
            var _a, _b;
            const minTier = (_b = (_a = tiersByRow[row]) === null || _a === void 0 ? void 0 : _a.minTier) !== null && _b !== void 0 ? _b : refTier;
            perRow[row].bottomBase =
                synthAnchor.baseMm + ((minTier - refTier) / 2) * CONTAINER_HEIGHT_IN_MMM;
        });
    }
    const getTierHeightMm = (row, tierIndex) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const rowTiers = tiersByRow[row];
        if (!rowTiers)
            return CONTAINER_HEIGHT_IN_MMM;
        const isoTier = pad2(rowTiers.minTier + tierIndex * 2);
        return ((_g = (_e = (_d = (_c = (_b = (_a = bl.perRowInfo) === null || _a === void 0 ? void 0 : _a.each) === null || _b === void 0 ? void 0 : _b[row]) === null || _c === void 0 ? void 0 : _c.minTierHeights) === null || _d === void 0 ? void 0 : _d[isoTier]) !== null && _e !== void 0 ? _e : (_f = common === null || common === void 0 ? void 0 : common.minTierHeights) === null || _f === void 0 ? void 0 : _f[isoTier]) !== null && _g !== void 0 ? _g : CONTAINER_HEIGHT_IN_MMM);
    };
    const layout = calculateAftBayLayout({ rows, tiersByRow, perRow, getTierHeightMm });
    if (!layout || layout.rows.length === 0)
        return undefined;
    return {
        level: bl.level,
        layout,
        tiersByRow,
        tcgsAreReal,
        bottomBasesAreReal,
        tierBottomBases: calculateTierBottomBases(layout),
    };
}
/** One bottom base per ISO tier: the most common value across the rows that have the tier */
function calculateTierBottomBases(layout) {
    const basesByTier = new Map();
    layout.rows.forEach((row) => {
        row.tierBasesMm.forEach((baseMm, tierIndex) => {
            const isoTier = Number(row.bottomTier) + tierIndex * 2;
            const base = roundDec(baseMm, 0);
            const list = basesByTier.get(isoTier);
            if (list)
                list.push(base);
            else
                basesByTier.set(isoTier, [base]);
        });
    });
    return Array.from(basesByTier.keys())
        .sort(sortNumericAsc)
        .map((isoTier) => ({
        tier: pad2(isoTier),
        bottomBaseMm: modeOf(basesByTier.get(isoTier)),
    }));
}
function modeOf(values) {
    const counts = new Map();
    let best = values[0];
    let bestCount = 0;
    values.forEach((v) => {
        var _a;
        const c = ((_a = counts.get(v)) !== null && _a !== void 0 ? _a : 0) + 1;
        counts.set(v, c);
        if (c > bestCount) {
            best = v;
            bestCount = c;
        }
    });
    return best;
}
function buildSections(shipData, sizeSummary, baysData, lidData) {
    const byBay = new Map();
    baysData.forEach((bl) => {
        var _a;
        const entry = (_a = byBay.get(bl.isoBay)) !== null && _a !== void 0 ? _a : {};
        if (bl.level === BayLevelEnum.ABOVE)
            entry.above = bl;
        else
            entry.below = bl;
        byBay.set(bl.isoBay, entry);
    });
    const baysUnderLids = (lidData !== null && lidData !== void 0 ? lidData : []).map((lid) => ({
        lid,
        isoBays: new Set(getIsoBaysUnderLid(lid)),
    }));
    const { belowAnchor, aboveAnchorFor } = calculateSyntheticAnchors(shipData.masterCGs, sizeSummary, baysData);
    const sortedIsoBays = Array.from(byBay.keys()).sort();
    // The holds first: the deck's synthetic anchor sits over the highest hold stack
    const belowLevels = new Map();
    sortedIsoBays.forEach((isoBay) => {
        var _a;
        const belowBl = (_a = byBay.get(isoBay)) === null || _a === void 0 ? void 0 : _a.below;
        const below = belowBl && buildSectionLevel(belowBl, shipData.masterCGs, sizeSummary, belowAnchor);
        if (below)
            belowLevels.set(isoBay, below);
    });
    const holdTops = Array.from(belowLevels.values()).map((lvl) => lvl.layout.maxBB);
    const aboveAnchor = aboveAnchorFor(holdTops.length ? Math.max(...holdTops) : undefined);
    const sectionsBySignature = new Map();
    sortedIsoBays.forEach((isoBay) => {
        var _a;
        const aboveBl = (_a = byBay.get(isoBay)) === null || _a === void 0 ? void 0 : _a.above;
        const above = aboveBl && buildSectionLevel(aboveBl, shipData.masterCGs, sizeSummary, aboveAnchor);
        const below = belowLevels.get(isoBay);
        if (!above && !below)
            return;
        const lids = baysUnderLids.filter((l) => l.isoBays.has(isoBay)).map((l) => l.lid);
        // The lids are part of the signature: bays with equal geometry but
        // different hatch covers must not share a drawn section.
        const signature = [levelSignature(above), levelSignature(below), lidsSignature(lids)].join("#");
        const existing = sectionsBySignature.get(signature);
        if (existing) {
            existing.isoBays.push(isoBay);
            return;
        }
        const levels = [above, below].filter(Boolean);
        const rows = levels
            .flatMap((lvl) => lvl.layout.rows.map((r) => r.row))
            .filter((row, idx, arr) => arr.indexOf(row) === idx)
            .sort(sortRowsArray);
        const rowXsMm = {};
        const rowTcgsMm = {};
        rows.forEach((row) => {
            var _a, _b;
            const fromLevel = levels.find((lvl) => lvl.layout.rows.some((r) => r.row === row));
            rowXsMm[row] = (_a = fromLevel === null || fromLevel === void 0 ? void 0 : fromLevel.layout.rows.find((r) => r.row === row)) === null || _a === void 0 ? void 0 : _a.tcg;
            // The TCG is only displayable when its source level has real TCGs
            const fromRealLevel = levels.find((lvl) => lvl.tcgsAreReal && lvl.layout.rows.some((r) => r.row === row));
            const realTcg = (_b = fromRealLevel === null || fromRealLevel === void 0 ? void 0 : fromRealLevel.layout.rows.find((r) => r.row === row)) === null || _b === void 0 ? void 0 : _b.tcg;
            if (realTcg !== undefined)
                rowTcgsMm[row] = realTcg;
        });
        sectionsBySignature.set(signature, {
            isoBays: [isoBay],
            above,
            below,
            rows,
            rowXsMm,
            rowTcgsMm,
            xMinMm: Math.min(...levels.map((lvl) => Math.min(...lvl.layout.rows.map((r) => r.prevTcgMid)))),
            xMaxMm: Math.max(...levels.map((lvl) => Math.max(...lvl.layout.rows.map((r) => r.nextTcgMid)))),
            minVcgMm: Math.min(...levels.map((lvl) => lvl.layout.minBB)),
            maxVcgMm: Math.max(...levels.map((lvl) => lvl.layout.maxBB)),
            lids,
        });
    });
    return Array.from(sectionsBySignature.values());
}
/**
 * The anchors of the synthesized bottom bases, so that a CG-less hold and deck
 * never overlap on the drawing: the hold starts at a fixed base (or, when the deck
 * has real bases, just under the lowest of them) and the deck starts just over the
 * highest hold stack (real or synthesized).
 */
function calculateSyntheticAnchors(masterCGs, sizeSummary, baysData) {
    // Vessel-wide tier ranges per level kind
    const tiers = {
        above: { min: Infinity, max: -Infinity },
        below: { min: Infinity, max: -Infinity },
    };
    // Lowest real deck bottom base of the vessel, when some deck level has real bases
    let realAboveMinBaseMm = Infinity;
    baysData.forEach((bl) => {
        const slotKeys = usableSlotKeys(bl, sizeSummary);
        if (slotKeys.length === 0)
            return;
        const kind = bl.level === BayLevelEnum.ABOVE ? "above" : "below";
        slotKeys.forEach((key) => {
            const tier = Number(key.substring(2));
            if (tier < tiers[kind].min)
                tiers[kind].min = tier;
            if (tier > tiers[kind].max)
                tiers[kind].max = tier;
        });
        if (bl.level === BayLevelEnum.ABOVE) {
            const { rows, tiersByRow } = getRowsAndTiersFromSlotKeys(slotKeys);
            const bases = rows.map((row) => resolveRowBottomBase(bl, row, tiersByRow, masterCGs));
            if (bases.every((b) => b !== undefined)) {
                realAboveMinBaseMm = Math.min(realAboveMinBaseMm, ...bases);
            }
        }
    });
    const belowTiersCount = Number.isFinite(tiers.below.min)
        ? (tiers.below.max - tiers.below.min) / 2 + 1
        : 0;
    // A synthesized hold under a real deck hangs from the deck's lowest base
    const belowBaseMm = Number.isFinite(realAboveMinBaseMm)
        ? realAboveMinBaseMm - SYNTHETIC_HATCH_CLEARANCE_MM - belowTiersCount * CONTAINER_HEIGHT_IN_MMM
        : SYNTHETIC_BELOW_BASE_MM;
    return {
        belowAnchor: { refTier: tiers.below.min, baseMm: belowBaseMm },
        aboveAnchorFor: (holdTopMm) => ({
            refTier: tiers.above.min,
            baseMm: holdTopMm !== undefined
                ? holdTopMm + SYNTHETIC_HATCH_CLEARANCE_MM
                : SYNTHETIC_ABOVE_BASE_MM,
        }),
    };
}
/** What makes two bays' hatch covers interchangeable on the drawing */
function lidsSignature(lids) {
    return lids
        .map((lid) => {
        var _a, _b;
        return `${lid.label}:${lid.portIsoRow}:${lid.starboardIsoRow}:${(_a = lid.overlapPort) !== null && _a !== void 0 ? _a : 0}:${(_b = lid.overlapStarboard) !== null && _b !== void 0 ? _b : 0}`;
    })
        .sort()
        .join("|");
}
/** Geometry signature of a bay-level, used to group identical bays into one drawn section */
function levelSignature(lvl) {
    if (!lvl)
        return "-";
    return (`${lvl.tcgsAreReal ? "T" : "t"}${lvl.bottomBasesAreReal ? "B" : "b"}` +
        lvl.layout.rows
            .map((r) => `${r.row}:${roundDec(r.tcg, 0)}:${roundDec(r.bottomBaseMm, 0)}:${r.bottomTier}:${r.numTiers}:${r.maxHeightMm !== undefined ? roundDec(r.maxHeightMm, 0) : "-"}`)
            .join("|"));
}
function buildBayCounts(baysData) {
    const { pairedBays } = getAllThePairedBays(baysData, false);
    const byBay = new Map();
    const getEntry = (isoBay) => {
        const existing = byBay.get(isoBay);
        if (existing)
            return existing;
        const entry = { isoBay, count20Above: 0, count20Below: 0 };
        byBay.set(isoBay, entry);
        return entry;
    };
    baysData.forEach((bl) => {
        var _a, _b;
        const entry = getEntry(bl.isoBay);
        const count20 = countSlots(bl, (sizes) => sizes.some((s) => s <= 24));
        const lcg20 = (_b = (_a = bl.infoByContLength) === null || _a === void 0 ? void 0 : _a[20]) === null || _b === void 0 ? void 0 : _b.lcg;
        if (bl.level === BayLevelEnum.ABOVE) {
            entry.count20Above = count20;
            entry.lcg20AboveMm = lcg20;
        }
        else {
            entry.count20Below = count20;
            entry.lcg20BelowMm = lcg20;
        }
    });
    const blsByBayAndLevel = new Map();
    baysData.forEach((bl) => blsByBayAndLevel.set(`${bl.isoBay}-${bl.level}`, bl));
    pairedBays.forEach((pair) => {
        var _a, _b, _c, _d, _e;
        const baseBl = blsByBayAndLevel.get(`${pair.base}-${pair.level}`);
        const pairedBl = blsByBayAndLevel.get(`${pair.paired}-${pair.level}`);
        const count40 = countPaired40Slots(baseBl, pairedBl);
        const lcg40 = (_c = (_b = (_a = baseBl === null || baseBl === void 0 ? void 0 : baseBl.infoByContLength) === null || _a === void 0 ? void 0 : _a[40]) === null || _b === void 0 ? void 0 : _b.lcg) !== null && _c !== void 0 ? _c : (_e = (_d = pairedBl === null || pairedBl === void 0 ? void 0 : pairedBl.infoByContLength) === null || _d === void 0 ? void 0 : _d[40]) === null || _e === void 0 ? void 0 : _e.lcg;
        // The pair is reported once, on its base (AFT) bay
        const entry = getEntry(pair.base);
        entry.pairedWith = pair.paired;
        if (pair.level === BayLevelEnum.ABOVE)
            entry.count40Above = count40;
        else
            entry.count40Below = count40;
        if (entry.lcg40Mm === undefined)
            entry.lcg40Mm = lcg40;
    });
    return Array.from(byBay.values()).sort((a, b) => a.isoBay.localeCompare(b.isoBay));
}
function countSlots(bl, sizesMatch) {
    var _a;
    const perSlotInfo = (_a = bl.perSlotInfo) !== null && _a !== void 0 ? _a : {};
    return Object.keys(perSlotInfo).reduce((acc, key) => {
        var _a;
        const slot = perSlotInfo[key];
        if (!slot || slot.restricted)
            return acc;
        return sizesMatch(Object.keys((_a = slot.sizes) !== null && _a !== void 0 ? _a : {}).map(Number)) ? acc + 1 : acc;
    }, 0);
}
/** Number of 40'-capable positions of a pair: union of the two bays' slot keys, counted once */
function countPaired40Slots(baseBl, pairedBl) {
    var _a, _b;
    const slotKeys = new Set([
        ...Object.keys((_a = baseBl === null || baseBl === void 0 ? void 0 : baseBl.perSlotInfo) !== null && _a !== void 0 ? _a : {}),
        ...Object.keys((_b = pairedBl === null || pairedBl === void 0 ? void 0 : pairedBl.perSlotInfo) !== null && _b !== void 0 ? _b : {}),
    ]);
    let count = 0;
    slotKeys.forEach((key) => {
        var _a, _b;
        const has40 = [(_a = baseBl === null || baseBl === void 0 ? void 0 : baseBl.perSlotInfo) === null || _a === void 0 ? void 0 : _a[key], (_b = pairedBl === null || pairedBl === void 0 ? void 0 : pairedBl.perSlotInfo) === null || _b === void 0 ? void 0 : _b[key]].some((slot) => {
            var _a;
            return slot &&
                !slot.restricted &&
                Object.keys((_a = slot.sizes) !== null && _a !== void 0 ? _a : {})
                    .map(Number)
                    .some((s) => s >= 40);
        });
        if (has40)
            count += 1;
    });
    return count;
}
function buildParticulars(shipData, baysData) {
    var _a;
    const above = baysData.filter((bl) => bl.level === BayLevelEnum.ABOVE);
    const below = baysData.filter((bl) => bl.level === BayLevelEnum.BELOW);
    return {
        shipName: shipData.shipName,
        lineOperator: shipData.lineOperator,
        callSign: shipData.callSign,
        imoCode: shipData.imoCode,
        yearBuilt: shipData.yearBuilt,
        loaMm: shipData.loa,
        lppMm: (_a = shipData.lcgOptions) === null || _a === void 0 ? void 0 : _a.lpp,
        teuOnDeck: calculateTeusFromSlots(above),
        teuInHold: calculateTeusFromSlots(below),
        teuTotal: calculateTeusFromSlots(baysData),
    };
}
